/**
 * The access token for the current session, for code that is not a component.
 *
 * `api.ts` is a plain module — it has no hooks and no provider above it — but
 * every call it makes needs the bearer token. PropelAuth's React SDK owns the
 * token and refreshes it, so the arrangement is: the provider pushes the
 * current value in here whenever it changes, and `request()` reads it.
 *
 * A module variable rather than a store, because there is exactly one session
 * per tab and nothing subscribes to it: the only reader is the line that builds
 * a header, and it wants whatever is current at the moment it runs.
 */
let token: string | null = null;

export function setAccessToken(next: string | null): void {
  token = next;
}

export function authHeader(): Record<string, string> {
  return token ? { authorization: `Bearer ${token}` } : {};
}

/**
 * What to do when the API says nobody is signed in.
 *
 * Registered by the Gate once PropelAuth is up, and called by `request()` on
 * any 401. Two situations reach it and they are not the same:
 *
 * A session that has expired mid-use — the token was fine when the page loaded
 * and is not now. Sending the browser to sign in again is the whole answer, and
 * it is what a user expects to happen without being told.
 *
 * And the portal not being configured for auth at all while the API is. Then
 * nothing is registered here, there is nowhere to send anybody, and the request
 * fails with the API's own words — "Sign in to continue" — on a screen with no
 * way to do that. `request()` says what is actually wrong instead.
 */
let onUnauthenticated: (() => Promise<unknown> | void) | null = null;

export function setUnauthenticatedHandler(handler: (() => Promise<unknown> | void) | null): void {
  onUnauthenticated = handler;
}

/**
 * Asks the provider to re-check the session, and says whether anybody was there
 * to ask.
 *
 * Awaited rather than fired and forgotten, because the caller wants to retry
 * once the answer is back. It used to return immediately and the caller hung
 * for ever on a promise that never settled — which made sense when this
 * redirected to a hosted sign-in page and the browser really was leaving. It
 * refreshes in place now, nothing leaves, and a caller that hangs leaves a
 * screen blank with a perfectly good session behind it.
 */
export async function handleUnauthenticated(): Promise<boolean> {
  if (!onUnauthenticated) return false;
  try {
    await onUnauthenticated();
  } catch {
    // A refresh that fails is a session that is genuinely gone. The caller
    // retries, gets a second 401, and reports it.
  }
  return true;
}
